import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"
import toasteventbus from "primevue/toasteventbus"
import { useFileManagerStore } from "./fileManager"
import { useDashboardStore } from "./dashboard"
import i18n from "@/languages/i18n"

const { t } = i18n.global

const createFileManagerItem = (documentId, documentData) => ({
  id: documentId,
  name: documentData.title,
  type: "file",
  size: documentData.size,
  mimeType: documentData.mime_type,
  extension: documentData.file_extension?.toLowerCase(),
  updated_at: documentData.updated_at || new Date().toISOString(),
  modified_by: documentData.modified_by,
  updater_name: documentData.updater_name,
  contractor: documentData.contractor,
  file_url: documentData.file_url,
  status: documentData.status,
  document_type_id: documentData.document_type_id,
  document_type_name: documentData.document_type_name,
  content: documentData.content,
  permissions: ["read", "write", "delete"]
})

const updateFileManagerItems = (fileManagerStore, documentId, documentData) => {
  const item = createFileManagerItem(documentId, documentData)
  fileManagerStore.items = fileManagerStore.items.map((doc) =>
    doc.id === documentId ? item : doc
  )
  fileManagerStore.allItems = fileManagerStore.allItems.map((doc) =>
    doc.id === documentId ? item : doc
  )
}

export const useDocumentsStore = defineStore("documents", {
  state: () => ({
    documents: [],
    selectedDocument: null,
    isLoading: false,
    extractionDocument: null,
    highlightedDocumentId: null,
    purgeWarning: null,
    userPreferencesData: null,
    analyticConnections: [],
    contractors: [],
    updatedByUsers: [],
    documentTypes: [],
    purgeFilters: null,
    originalDocumentContent: null,
    originalDocumentFileName: null,
    hasUnsavedChanges: false,
    pagination: {
      current_page: 1,
      per_page: 25,
      total: 0,
      from: 0,
      to: 0
    }
  }),

  getters: {
    hasDocuments: (state) => state.documents.length > 0,
    getCurrentDocument: (state) => state.selectedDocument,
    hasPagination: (state) => state.pagination.total > 25,
    totalPages: (state) =>
      Math.ceil(state.pagination.total / state.pagination.per_page)
  },

  actions: {
    async getDocuments(params) {
      this.isLoading = true
      try {
        const requestParams = { ...params }

        if (this.pagination.current_page) {
          requestParams.page = this.pagination.current_page
        }
        if (this.pagination.per_page) {
          requestParams.per_page = this.pagination.per_page
        }

        const { data } = await apiV1.get("/manage-documents", {
          params: requestParams
        })

        if (data.data && data.data.current_page) {
          this.pagination = {
            current_page: data.data.current_page || 1,
            per_page: data.data.per_page || 25,
            total: data.data.total || 0,
            from: data.data.from || 0,
            to: data.data.to || 0
          }
        } else {
          this.pagination = {
            ...this.pagination,
            total: (data.documents || data).length,
            from: (data.documents || data).length > 0 ? 1 : 0,
            to: (data.documents || data).length
          }
        }

        this.documents = data.data.data
      } finally {
        this.isLoading = false
      }
    },

    async getDocument(documentId) {
      this.isLoading = true
      try {
        const { data } = await apiV1.get(`/documents/${documentId}`)
        this.selectedDocument = data.data || data
        this.storeOriginalDocument()
        return this.selectedDocument
      } finally {
        this.isLoading = false
      }
    },

    async deleteDocument(documentId) {
      try {
        await apiV1.delete(`/documents/${documentId}`)
        this.documents = this.documents.filter((doc) => doc.id !== documentId)
        if (this.selectedDocument?.id === documentId) {
          this.selectedDocument = null
        }
      } finally {
        this.isLoading = false
      }
    },

    setSelectedDocument(document) {
      this.selectedDocument = document
      this.storeOriginalDocument()
    },

    updateSelectedDocument(document) {
      this.selectedDocument = document
      this.documents = this.documents.map((doc) =>
        doc.id === document.id ? document : doc
      )
    },

    clearSelectedDocument() {
      this.selectedDocument = null
      this.clearOriginalDocument()
    },

    async updateDocumentContent(documentId, documentContent, fileName = null) {
      const payload = {
        content: documentContent
      }

      if (fileName) {
        payload.fileName = fileName
      }

      await apiV1.put(`/documents/${documentId}/content`, payload)
      const document = await this.getDocument(documentId)
      this.selectedDocument = document
      this.clearUnsavedChanges()
    },

    async processDocument(documentId, documentTypeId, fileName = "") {
      await apiV1.post(`/documents/${documentId}/process`, {
        document_type_id: documentTypeId,
        fileName: fileName
      })
    },

    async exportDocuments(params) {
      const { data } = await apiV1.post("/documents/export", null, { params })
      return data
    },

    async downloadDocument(documentId, params) {
      const { data } = await apiV1.get(`/documents/${documentId}/download`, {
        params
      })

      if (data.download_url) {
        window.open(data.download_url, "_blank")
      }

      return data
    },

    async createDocument(documentData) {
      const response = await apiV1.post("/documents/content", documentData)
      return response.data
    },

    async getDocumentAfterProcessing(documentId) {
      if (!documentId) return

      const { data } = await apiV1.get(`/documents/${documentId}`)
      const fileManagerStore = useFileManagerStore()
      const dashboardStore = useDashboardStore()

      if (this.selectedDocument?.id === documentId) {
        this.selectedDocument = data.data
      }

      this.documents = this.documents.map((doc) =>
        doc.id === documentId ? data.data : doc
      )

      updateFileManagerItems(fileManagerStore, documentId, data.data)

      await this.getAnalyticConnections()
      await this.getContractors()
      await this.getUpdatedByUsers()
      await this.getDocumentTypes()
      await dashboardStore.getHubUsers()

      if (data.data.status === "processed") {
        toasteventbus.emit("add", {
          severity: "success",
          summary: t("views.manageDocuments.toasts.documentProcessedSummary"),
          detail: t("views.manageDocuments.toasts.documentProcessedDetail"),
          life: 3000
        })
      } else if (data.data.status === "failed") {
        toasteventbus.emit("add", {
          severity: "error",
          summary: t("views.manageDocuments.toasts.documentFailedSummary"),
          detail: t("views.manageDocuments.toasts.documentFailedDetail"),
          life: 3000
        })
      }

      return data.data
    },

    async handleDocumentMessagesUpdated(
      documentId,
      flagMessages,
      validationRule = false
    ) {
      if (!documentId) return

      const { data } = await apiV1.get(`/documents/${documentId}`)
      const fileManagerStore = useFileManagerStore()

      if (this.selectedDocument?.id === documentId) {
        this.selectedDocument = data.data
      }

      this.documents = this.documents.map((doc) =>
        doc.id === documentId ? data.data : doc
      )

      updateFileManagerItems(fileManagerStore, documentId, data.data)

      if (flagMessages && !validationRule) {
        Object.values(flagMessages).forEach((message) => {
          toasteventbus.emit("add", {
            severity: "info",
            summary: t("views.manageDocuments.toasts.documentUpdatedSummary"),
            detail: message,
            life: 5000
          })
        })
      }

      return data.data
    },

    async connectDocument(documentId, externalSystemId) {
      const { data } = await apiV1.post(`/document-extractions`, {
        document_id: documentId,
        external_system_id: externalSystemId
      })
      return data
    },

    /**
     * @deprecated The `externalSystemId` parameter is a legacy analytic_connection_id.
     * V2 extractions will be scoped by connector_slug instead. This method continues
     * to work while DocumentAnalytic.vue still uses `?system=` query routing.
     */
    async getExtractionDocument(documentId, externalSystemId) {
      const { data } = await apiV1.get(`/document-extractions/${documentId}`, {
        params: { external_system_id: externalSystemId }
      })
      this.extractionDocument = data
      return data
    },

    /**
     * @deprecated The `externalSystemId` parameter is a legacy analytic_connection_id.
     * V2 will use connector_slug for scoping document extractions.
     */
    async editExtractionDocument(documentId, extractionData, externalSystemId) {
      await apiV1.put(`/document-extractions/${documentId}`, {
        data: extractionData,
        external_system_id: externalSystemId
      })
    },

    async renameDocument(documentId, newName) {
      const { data } = await apiV1.put(`/documents/${documentId}`, {
        title: newName
      })
      const docIndex = this.documents.findIndex((doc) => doc.id === documentId)
      if (docIndex !== -1) {
        this.documents[docIndex].title = newName
      }
      if (this.selectedDocument?.id === documentId) {
        this.selectedDocument.title = newName
      }
      return data
    },

    async moveDocument(documentId, targetFolderId = null) {
      const { data } = await apiV1.put(`/documents/${documentId}/move`, {
        folder_id: targetFolderId
      })
      return data
    },

    async getPurgeWarning() {
      const { data } = await apiV1.get("documents/purge-warning")
      this.purgeWarning = data
      return data
    },

    async verifyDocument(documentId) {
      const { data } = await apiV1.patch(`/documents/${documentId}/verify`)
      return data
    },

    async resetDocument(documentId) {
      await apiV1.post(`/documents/${documentId}/reset`)
      const document = await this.getDocument(documentId)
      this.selectedDocument = document
      return document
    },

    setDocuments(documents) {
      this.documents = documents
    },

    setHighlightedDocumentId(id) {
      this.highlightedDocumentId = id
    },
    clearHighlightedDocumentId() {
      this.highlightedDocumentId = null
    },

    async loadUserPreferences() {
      const response = await apiV1.get("/manage-documents/user-preferences")
      this.userPreferencesData = response.data
      return response.data
    },

    async saveUserPreferences(columnOrder, selectedColumns) {
      if (!this.userPreferencesData) {
        throw new Error("No user preferences data loaded")
      }

      const updatedPreferences = {
        ...this.userPreferencesData,
        column_order: columnOrder,
        visible_columns: selectedColumns
      }

      await apiV1.post("/manage-documents/user-preferences", updatedPreferences)
      this.userPreferencesData = updatedPreferences
    },

    getDefaultSettings() {
      return this.userPreferencesData?.default_settings || null
    },

    async bulkDeleteDocuments(documentIds) {
      await apiV1.delete("/manage-documents/bulk-delete", {
        data: { document_ids: documentIds }
      })
      this.documents = this.documents.filter(
        (doc) => !documentIds.includes(doc.id)
      )
    },

    async bulkProcessDocuments(documentIds, documentTypeId) {
      const response = await apiV1.post("/manage-documents/bulk-process", {
        document_ids: documentIds,
        document_type_id: documentTypeId
      })
      return response.data
    },

    async bulkExportDocuments(documentIds, format = "xlsx") {
      const response = await apiV1.post("/manage-documents/bulk-export", {
        document_ids: documentIds,
        format: format
      })

      if (response.data.download_url) {
        window.open(response.data.download_url, "_blank")
      }

      return response.data
    },

    /**
     * @deprecated since V2. The analytic-connections API is superseded by the
     * connector system. Use the connectors store (`src/store/connectors.js`)
     * to fetch active connectors instead. Kept for the manage-documents filter
     * dropdown until that view is migrated.
     */
    async getAnalyticConnections() {
      const response = await apiV1.get("/manage-documents/analytic-connections")
      this.analyticConnections = response.data
      return response.data
    },

    async getUpdatedByUsers() {
      const response = await apiV1.get("/manage-documents/updated-by-users")
      this.updatedByUsers = response.data
      return response.data
    },

    async getContractors() {
      const response = await apiV1.get("/manage-documents/contractors")
      this.contractors = response.data
      return response.data
    },

    async getDocumentTypes() {
      const response = await apiV1.get("/manage-documents/document-types")
      this.documentTypes = response.data
      return response.data
    },

    setPurgeFilters(filters) {
      this.purgeFilters = filters
    },

    getPurgeFilters() {
      return this.purgeFilters
    },

    clearPurgeFilters() {
      this.purgeFilters = null
    },

    async getMassAnalyticFields(analyticConnectionId, documentTypeId) {
      const params = { analytic_connection_id: analyticConnectionId }
      if (documentTypeId) {
        params.document_type_id = documentTypeId
      }

      const response = await apiV1.get("/document-extractions/mass-analytic", {
        params
      })
      return response.data
    },

    async submitMassAnalytics(payload) {
      const response = await apiV1.post(
        "/document-extractions/mass-analytic",
        payload
      )
      return response.data
    },

    setPage(page) {
      this.pagination.current_page = page
    },

    setPerPage(perPage) {
      this.pagination.per_page = perPage
      this.pagination.current_page = 1
      this.pagination.from = 0
      this.pagination.to = 0
    },

    resetPagination() {
      this.pagination.current_page = 1
      this.pagination.per_page = 25
      this.pagination.total = 0
      this.pagination.from = 0
      this.pagination.to = 0
    },

    storeOriginalDocument() {
      if (this.selectedDocument) {
        this.originalDocumentContent = this.selectedDocument.content
          ? JSON.stringify(this.selectedDocument.content)
          : null
        this.originalDocumentFileName = this.selectedDocument.title || null
        this.hasUnsavedChanges = false
      }
    },

    clearOriginalDocument() {
      this.originalDocumentContent = null
      this.originalDocumentFileName = null
      this.hasUnsavedChanges = false
    },

    markAsChanged() {
      this.hasUnsavedChanges = true
    },

    clearUnsavedChanges() {
      this.hasUnsavedChanges = false
      this.storeOriginalDocument()
    },

    checkForChanges(currentContent, currentFileName) {
      if (!this.selectedDocument) {
        this.hasUnsavedChanges = false
        return false
      }

      const contentChanged =
        this.originalDocumentContent &&
        JSON.stringify(currentContent) !== this.originalDocumentContent

      const fileNameChanged =
        this.originalDocumentFileName &&
        currentFileName !== this.originalDocumentFileName

      this.hasUnsavedChanges = contentChanged || fileNameChanged
      return this.hasUnsavedChanges
    }
  }
})
